import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Button, Alert, Spinner, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import { getWordPairsWithWords } from '../../../services/gameService';
import { getProblemSounds } from '../../../services/pronunciationTestService';
import { auth } from '../../../firebase';
import Icon from '../../UI/Icon';
import PhoneUnavailableMessage from '../../UI/PhoneUnavailableMessage';
import { LuGamepad2 } from "react-icons/lu";
import sprechheldenbubble from '../../../assets/sprechheldenHeld.png';
import gameWon from '../../../assets/gameWon.png';
import helpLogo from '../../../assets/logo_logo_sh.png';
import background1 from '../../../assets/background_1.png';
import Confetti from 'react-confetti';
import party from 'party-js';
import { isPhone as checkIsPhone, isTabletPortrait, setupDeviceDetection } from '../../../utils/deviceDetection';

function WordMatchGame({ selectedSyllables, onBackToGames }) {
  const [leftWords, setLeftWords] = useState([]);
  const [rightWords, setRightWords] = useState([]);
  const [gameComplete, setGameComplete] = useState(false);
  const [wordPairs, setWordPairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [images, setImages] = useState({});
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [animatingPairs, setAnimatingPairs] = useState([]); // Pairs currently animating out
  const [incorrectPairs, setIncorrectPairs] = useState([]); // Cards that were incorrectly matched
  const [draggedCard, setDraggedCard] = useState(null);
  const [dragOverCard, setDragOverCard] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const sparkleIntervalRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [matchedWords, setMatchedWords] = useState(null);
  const [pendingGameComplete, setPendingGameComplete] = useState(false);
  const [revealedHints, setRevealedHints] = useState([]); // Array of pair IDs that are currently being shown as hints
  const [touchStartCard, setTouchStartCard] = useState(null); // For touch support
  const [dragPreview, setDragPreview] = useState(null); // For visual drag preview on touch
  const [isPhone, setIsPhone] = useState(false);
  const [isTabletPortraitMode, setIsTabletPortraitMode] = useState(false);
  const [isTabletOrSmaller, setIsTabletOrSmaller] = useState(window.innerWidth <= 1024);
  const [showLautModal, setShowLautModal] = useState(true); // For laut selection modal
  const [selectedLauts, setSelectedLauts] = useState([]); // Selected lauts to filter by
  const [availableLauts, setAvailableLauts] = useState([]); // All available lauts from word_connections
  const [allWordPairs, setAllWordPairs] = useState([]); // Store all word pairs before filtering
  
  const containerRef = useRef(null);

  // Track pointer position during drag using multiple event types
  const updateMousePosition = useCallback((event) => {
    if (draggedCard && event.clientX > 0 && event.clientY > 0) {
      setMousePosition({ x: event.clientX, y: event.clientY });
    }
  }, [draggedCard]);

  // Track touch position during drag
  const updateTouchPosition = useCallback((event) => {
    if (touchStartCard && event.touches && event.touches[0]) {
      const touch = event.touches[0];
      const newPosition = { x: touch.clientX, y: touch.clientY };
      setMousePosition(newPosition);
      
      // Update drag preview position
      if (dragPreview) {
        setDragPreview(prev => ({
          ...prev,
          x: newPosition.x - 60, // Center the preview on finger
          y: newPosition.y - 60
        }));
      }
    }
  }, [touchStartCard, dragPreview]);

  // Prevent scrolling during touch drag operations
  const preventScrolling = useCallback((event) => {
    if (touchStartCard) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, [touchStartCard]);

  // Fetch image pairs from database
  const fetchWordPairs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const allPairs = await getWordPairsWithWords();
      
      if (!allPairs || allPairs.length === 0) {
        console.warn('⚠️ No word pairs returned from database');
        setError('Keine Wortpaare gefunden. Bitte versuchen Sie es erneut.');
        return;
      }
      
      // Store all word pairs for later filtering
      setAllWordPairs(allPairs);
      
      // Extract all unique lauts from word_connections
      const lautsSet = new Set();
      allPairs.forEach(pair => {
        if (pair.leftWordData?.laut && Array.isArray(pair.leftWordData.laut)) {
          pair.leftWordData.laut.forEach(l => lautsSet.add(l.toLowerCase()));
        }
        if (pair.rightWordData?.laut && Array.isArray(pair.rightWordData.laut)) {
          pair.rightWordData.laut.forEach(l => lautsSet.add(l.toLowerCase()));
        }
      });
      
      const uniqueLauts = Array.from(lautsSet).sort();
      setAvailableLauts(uniqueLauts);

      // Auto-select user's severe problem sounds
      const user = auth.currentUser;
      if (user) {
        // Use getProblemSounds service to get sounds from pronunciation tests collection
        const problemSoundsData = await getProblemSounds(user.uid);
        const severeSounds = problemSoundsData.severeProblemSounds;

        if (severeSounds && Array.isArray(severeSounds) && severeSounds.length > 0) {
          // Pre-select severe problem sounds that are available in the game (case-insensitive)
          const soundsToPreselect = severeSounds.filter(sound =>
            uniqueLauts.some(laut => laut.toLowerCase() === sound.toLowerCase())
          ).map(sound => {
            // Return the actual laut from available lauts (with correct casing)
            return uniqueLauts.find(laut => laut.toLowerCase() === sound.toLowerCase());
          }).filter(Boolean);

          // Limit to max 3 sounds for WordMatch game
          const limitedSounds = soundsToPreselect.slice(0, 3);
          setSelectedLauts(limitedSounds);
        }
      }

      // Don't set word pairs yet - wait for laut selection
      if (!showLautModal) {
        // Select up to 5 pairs ensuring no duplicate words across pairs
        const shuffled = allPairs.sort(() => Math.random() - 0.5);
        const selectedPairs = [];
        const usedWords = new Set();
        for (const pair of shuffled) {
          if (usedWords.has(pair.left) || usedWords.has(pair.right)) continue;
          usedWords.add(pair.left);
          usedWords.add(pair.right);
          selectedPairs.push(pair);
          if (selectedPairs.length >= 5) break;
        }

        if (selectedPairs.length < 5) {
          console.warn(`Only ${selectedPairs.length} unique image pairs available. Consider adding more pairs.`);
        }

        setWordPairs(selectedPairs);
      }

      // Build image map from all word pairs data (images are already included)
      const imageMap = {};
      allPairs.forEach(pair => {
        if (pair.leftImage) {
          imageMap[pair.left] = { linkedImage: pair.leftImage };
        }
        if (pair.rightImage) {
          imageMap[pair.right] = { linkedImage: pair.rightImage };
        }
      });
      setImages(imageMap);
    } catch (err) {
      console.error('❌ Error fetching image pairs:', err);
      setError('Fehler beim Laden der Bildpaare. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const initializeGame = () => {
    const shuffledLeft = wordPairs.map((pair, index) => ({
      id: `left-${index}`,
      word: pair.left,
      pairId: index,
      correctMatchId: `right-${index}`
    })).sort(() => Math.random() - 0.5);

    const shuffledRight = wordPairs.map((pair, index) => ({
      id: `right-${index}`,
      word: pair.right,
      pairId: index,
      correctMatchId: `left-${index}`
    })).sort(() => Math.random() - 0.5);

    setLeftWords(shuffledLeft);
    setRightWords(shuffledRight);
    setMatchedPairs([]);
    setAnimatingPairs([]);
    setIncorrectPairs([]);
    setGameComplete(false);
    setDraggedCard(null);
    setDragOverCard(null);
    setShowConfetti(false);
    setMatchedWords(null);
    setRevealedHints([]);
  };

  // Fetch image pairs when component mounts
  useEffect(() => {
    fetchWordPairs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize game when image pairs are loaded
  useEffect(() => {
    if (wordPairs.length > 0) {
      initializeGame();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wordPairs, selectedSyllables]);

  // Drag and drop handlers
  const handleDragStart = useCallback((event, wordId, side) => {
    const wordData = side === 'left' 
      ? leftWords.find(w => w.id === wordId)
      : rightWords.find(w => w.id === wordId);
    
    if (!wordData || matchedPairs.includes(wordData.id)) {
      event.preventDefault();
      return false;
    }

    const dragData = { id: wordId, side, word: wordData.word, pairId: wordData.pairId };
    setDraggedCard(dragData);
    
    // Set drag data
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', JSON.stringify(dragData));
    
    // Create a custom drag image to ensure visibility
    const dragImage = event.currentTarget.cloneNode(true);
    dragImage.style.transform = 'scale(1.1)';
    dragImage.style.opacity = '0.9';
    dragImage.style.background = '#e3f2fd';
    dragImage.style.borderColor = '#2196f3';
    dragImage.style.borderWidth = '3px';
    dragImage.style.boxShadow = '0 8px 25px rgba(33, 150, 243, 0.4)';
    
    // Temporarily add to document for drag image
    dragImage.style.position = 'absolute';
    dragImage.style.top = '-1000px';
    dragImage.style.left = '-1000px';
    document.body.appendChild(dragImage);
    
    // Set the custom drag image
    event.dataTransfer.setDragImage(dragImage, 60, 60);
    
    // Clean up the temporary element after a short delay
    setTimeout(() => {
      if (document.body.contains(dragImage)) {
        document.body.removeChild(dragImage);
      }
    }, 0);
    
    // Use event.currentTarget instead of event.target to get the card div
    const element = event.currentTarget;
    
    if (element && element instanceof Element && document.contains(element)) {
      // Add dragging class
      element.classList.add('dragging');
      
      // Start tracking mouse position using multiple event strategies
      setMousePosition({ x: event.clientX, y: event.clientY });
      
      // Add global event listeners for tracking (mouse only)
      document.addEventListener('pointermove', updateMousePosition, { passive: true });
      document.addEventListener('mousemove', updateMousePosition, { passive: true });
      window.addEventListener('dragover', updateMousePosition, { passive: true });
      
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        // Double-check element is still in DOM
        if (document.contains(element)) {
          try {
            // Create a temporary invisible element at mouse position for sparkles
            const tempElement = document.createElement('div');
            tempElement.style.cssText = `
              position: fixed;
              left: ${event.clientX}px;
              top: ${event.clientY}px;
              width: 1px;
              height: 1px;
              pointer-events: none;
              z-index: 9999;
            `;
            document.body.appendChild(tempElement);
            
            party.sparkles(tempElement, {
              count: party.variation.range(15, 25),
              speed: party.variation.range(100, 300)
            });
            
            // Remove temp element after a short delay
            setTimeout(() => {
              if (tempElement.parentNode) {
                tempElement.parentNode.removeChild(tempElement);
              }
            }, 50);
          } catch (error) {
            console.error('❌ party.sparkles error:', error);
          }
        }
      });
    }
  }, [leftWords, rightWords, matchedPairs]);

  const handleDragEnd = useCallback((event) => {
    const element = event.currentTarget;
    if (element && element.classList) {
      element.classList.remove('dragging');
    }
    
    // Stop tracking mouse position
    document.removeEventListener('pointermove', updateMousePosition);
    document.removeEventListener('mousemove', updateMousePosition);
    window.removeEventListener('dragover', updateMousePosition);
    
    setDraggedCard(null);
    setDragOverCard(null);
  }, [updateMousePosition]);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    
    // Update mouse position during dragover as backup
    updateMousePosition(event);
  }, [updateMousePosition]);

  const handleDragEnter = useCallback((event, wordId, side) => {
    event.preventDefault();
    
    const targetWordData = side === 'left' 
      ? leftWords.find(w => w.id === wordId)
      : rightWords.find(w => w.id === wordId);

    if (targetWordData && !matchedPairs.includes(targetWordData.id)) {
      setDragOverCard({ id: wordId, side });
    }
  }, [leftWords, rightWords, matchedPairs]);

  const handleDragLeave = useCallback((event) => {
    // Only clear drag over if we're actually leaving the card
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setDragOverCard(null);
    }
  }, []);

  const handleDrop = useCallback((event, targetWordId, targetSide) => {
    event.preventDefault();
    
    let dragData;
    try {
      const dragDataString = event.dataTransfer.getData('text/plain');
      dragData = dragDataString ? JSON.parse(dragDataString) : draggedCard;
    } catch (e) {
      dragData = draggedCard;
    }
    
    if (!dragData) {
      return;
    }

    const targetWordData = targetSide === 'left' 
      ? leftWords.find(w => w.id === targetWordId)
      : rightWords.find(w => w.id === targetWordId);

    if (!targetWordData || matchedPairs.includes(targetWordData.id)) {
      return;
    }

    // Don't allow dropping on the same card
    if (dragData.id === targetWordId) {
      return;
    }

    // Check if it's a correct match - look for any valid pair combination
    const isCorrect = wordPairs.some(pair => 
      (pair.left === dragData.word && pair.right === targetWordData.word) ||
      (pair.right === dragData.word && pair.left === targetWordData.word)
    );

    if (isCorrect) {
      // First add to animating pairs for the shrink animation
      setAnimatingPairs(prev => [...prev, dragData.id, targetWordId]);
      
      // Update speech bubble text to show the matched words
      setMatchedWords({ left: dragData.word, right: targetWordData.word });
      
      // Create celebratory sparkles at both card locations
      const sourceCard = document.querySelector(`[key="${dragData.id}"]`) || event.target;
      const targetCard = document.querySelector(`[key="${targetWordId}"]`) || event.currentTarget;
      
      if (sourceCard) {
        try {
          party.sparkles(sourceCard, {
            count: party.variation.range(20, 30),
            speed: party.variation.range(100, 300)
          });
        } catch (e) {
          // Silently ignore sparkles error
        }
      }
      
      if (targetCard) {
        try {
          party.sparkles(targetCard, {
            count: party.variation.range(20, 30),
            speed: party.variation.range(100, 300)
          });
        } catch (e) {
          // Silently ignore sparkles error
        }
      }
      
      // After animation completes, add to matched pairs
      setTimeout(() => {
        setMatchedPairs(prev => {
          const newMatched = [...prev, dragData.id, targetWordId];
          
          // Check if game is complete (all cards matched)
          const totalCards = leftWords.length + rightWords.length;
          if (newMatched.length === totalCards) {
            setPendingGameComplete(true); // Don't show confetti yet, wait for WEITER button
          }
          
          return newMatched;
        });
        
        // Remove from animating pairs
        setAnimatingPairs(prev => prev.filter(id => id !== dragData.id && id !== targetWordId));
        
        // Clear any hints for matched cards
        setRevealedHints(prev => prev.filter(id => {
          const leftCard = leftWords.find(w => w.id === dragData.id || w.id === targetWordId);
          const rightCard = rightWords.find(w => w.id === dragData.id || w.id === targetWordId);
          return leftCard ? id !== leftCard.pairId : (rightCard ? id !== rightCard.pairId : true);
        }));
      }, 600); // 600ms for the animation to complete
    } else {
      // Handle incorrect match
      // Add both cards to incorrect pairs for visual feedback
      setIncorrectPairs(prev => [...prev, dragData.id, targetWordId]);
      
      // Remove incorrect state after 1 second
      setTimeout(() => {
        setIncorrectPairs(prev => prev.filter(id => id !== dragData.id && id !== targetWordId));
      }, 1000);
    }

    // Reset drag state
    setDraggedCard(null);
    setDragOverCard(null);
  }, [draggedCard, leftWords, rightWords, matchedPairs, wordPairs.length]);

  // Touch event handlers for iPad support
  const handleTouchStart = useCallback((event, wordId, side) => {
    const wordData = side === 'left' 
      ? leftWords.find(w => w.id === wordId)
      : rightWords.find(w => w.id === wordId);
    
    if (!wordData || matchedPairs.includes(wordData.id)) {
      event.preventDefault();
      return false;
    }

    // Prevent all default touch behaviors early to stop scrolling
    event.preventDefault();
    event.stopPropagation();

    const touchData = { id: wordId, side, word: wordData.word, pairId: wordData.pairId };
    setTouchStartCard(touchData);
    setDraggedCard(touchData);
    
    // Disable scrolling on the document body during drag
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.touchAction = 'none';
    
    // Add dragging class
    const element = event.currentTarget;
    if (element && element.classList) {
      element.classList.add('dragging');
    }
    
    // Track touch position
    const touch = event.touches[0];
    if (touch) {
      const position = { x: touch.clientX, y: touch.clientY };
      setMousePosition(position);
      
      // Create visual drag preview for touch
      const imageUrl = getImageUrl(wordData.word);
      setDragPreview({
        x: position.x - 60,
        y: position.y - 60,
        word: wordData.word,
        imageUrl: imageUrl
      });
      
      // Add global touch move listener for smooth tracking with passive: false to prevent scrolling
      document.addEventListener('touchmove', updateTouchPosition, { passive: false });
      
      // Add additional document-level listeners to prevent any scrolling
      document.addEventListener('touchmove', preventScrolling, { passive: false });
      document.addEventListener('touchforcechange', preventScrolling, { passive: false });
    }
  }, [leftWords, rightWords, matchedPairs, images, updateTouchPosition]);

  const handleTouchMove = useCallback((event) => {
    if (touchStartCard) {
      event.preventDefault(); // Prevent scrolling
      const touch = event.touches[0];
      if (touch) {
        const newPosition = { x: touch.clientX, y: touch.clientY };
        setMousePosition(newPosition);
        
        // Update drag preview position to follow finger
        if (dragPreview) {
          setDragPreview(prev => ({
            ...prev,
            x: newPosition.x - 60, // Center the preview on finger
            y: newPosition.y - 60
          }));
        }
      }
    }
  }, [touchStartCard, dragPreview]);

  const handleTouchEnd = useCallback((event) => {
    if (!touchStartCard) {
      return;
    }

    event.preventDefault();
    
    // Re-enable scrolling on the document body
    document.body.style.overflow = '';
    document.body.style.touchAction = '';
    document.documentElement.style.overflow = '';
    document.documentElement.style.touchAction = '';
    
    // Find the element under the touch point
    const touch = event.changedTouches[0];
    if (touch) {
      
      // Try multiple points around the touch to improve detection accuracy
      const points = [
        { x: touch.clientX, y: touch.clientY }, // Center
        { x: touch.clientX - 5, y: touch.clientY }, // Left
        { x: touch.clientX + 5, y: touch.clientY }, // Right
        { x: touch.clientX, y: touch.clientY - 5 }, // Top
        { x: touch.clientX, y: touch.clientY + 5 }  // Bottom
      ];
      
      let elementBelow = null;
      for (const point of points) {
        elementBelow = document.elementFromPoint(point.x, point.y);
        if (elementBelow) break;
      }
      
      // Find the word card that contains this element
      let targetCard = elementBelow;
      while (targetCard && targetCard !== document.body) {
        if (targetCard.classList && targetCard.classList.contains('word-card')) {
          break;
        }
        targetCard = targetCard.parentElement;
      }
      
      // If no card found with DOM traversal, try direct element selection at touch point
      if (!targetCard || !targetCard.classList.contains('word-card')) {
        const allCards = document.querySelectorAll('.word-card');
        for (const card of allCards) {
          const rect = card.getBoundingClientRect();
          if (touch.clientX >= rect.left && touch.clientX <= rect.right &&
              touch.clientY >= rect.top && touch.clientY <= rect.bottom) {
            targetCard = card;
            break;
          }
        }
      }
      
      if (targetCard) {
        let targetWordData = null;
        let targetSide = null;
        
        // Primary method: Use data attributes for direct identification
        const wordId = targetCard.getAttribute('data-word-id');
        const wordSide = targetCard.getAttribute('data-word-side');
        // Note: wordText attribute exists on card but not used in this method
        
        if (wordId && wordSide) {
          // Find the word data using the word ID
          const allWords = [...leftWords, ...rightWords];
          targetWordData = allWords.find(word => word.id === wordId);
          targetSide = wordSide;
          
          if (targetWordData) {
            if (isCardMatched(targetWordData.id)) {
              targetWordData = null;
              targetSide = null;
            }
          }
        }
        
        // Fallback method: Position-based detection
        if (!targetWordData) {
          const leftCardContainer = document.querySelector('.word-column:first-child .word-row');
          const rightCardContainer = document.querySelector('.word-column:last-child .word-row');
          
          // Check if the target card is in the left column
          if (leftCardContainer && leftCardContainer.contains(targetCard)) {
            targetSide = 'left';
            // Find the index of this card within the left container
            const leftCards = Array.from(leftCardContainer.querySelectorAll('.word-card'));
            const cardIndex = leftCards.indexOf(targetCard);
            if (cardIndex >= 0 && cardIndex < leftWords.length) {
              // Get the word data by finding the non-matched card at this visual position
              const visibleLeftWords = leftWords.filter(word => !isCardMatched(word.id));
              if (cardIndex < visibleLeftWords.length) {
                targetWordData = visibleLeftWords[cardIndex];
              }
            }
          }
          // Check if the target card is in the right column
          else if (rightCardContainer && rightCardContainer.contains(targetCard)) {
            targetSide = 'right';
            // Find the index of this card within the right container
            const rightCards = Array.from(rightCardContainer.querySelectorAll('.word-card'));
            const cardIndex = rightCards.indexOf(targetCard);
            if (cardIndex >= 0 && cardIndex < rightWords.length) {
              // Get the word data by finding the non-matched card at this visual position
              const visibleRightWords = rightWords.filter(word => !isCardMatched(word.id));
              if (cardIndex < visibleRightWords.length) {
                targetWordData = visibleRightWords[cardIndex];
              }
            }
          }
        }
        
        // Final fallback: use the original content-based matching
        if (!targetWordData) {
          const allWords = [...leftWords, ...rightWords];
          const cardText = targetCard.textContent?.trim();
          const cardImg = targetCard.querySelector('img');
          const imgAlt = cardImg?.alt;
          const imgSrc = cardImg?.src;
          
          targetWordData = allWords.find(word => {
            const isMatched = isCardMatched(word.id);
            const textMatch = cardText === word.word;
            const altMatch = imgAlt === word.word;
            const expectedUrl = getImageUrl(word.word);
            const srcMatch = imgSrc && expectedUrl && (
              imgSrc.includes(expectedUrl.split('/').pop()) || 
              imgSrc === expectedUrl ||
              imgSrc.endsWith(expectedUrl.split('/').pop())
            );
            
            return !isMatched && (textMatch || altMatch || srcMatch);
          });
          
          if (targetWordData) {
            targetSide = leftWords.includes(targetWordData) ? 'left' : 'right';
          }
        }
        
        if (targetWordData && targetSide) {
          // Simulate drop
          handleTouchDrop(targetWordData.id, targetSide);
        }
      }
    }
    
    // Clean up
    const draggedElement = document.querySelector('.word-card.dragging');
    if (draggedElement) {
      draggedElement.classList.remove('dragging');
    }
    
    // Remove all global touch event listeners
    document.removeEventListener('touchmove', updateTouchPosition);
    document.removeEventListener('touchmove', preventScrolling);
    document.removeEventListener('touchforcechange', preventScrolling);
    
    setTouchStartCard(null);
    setDraggedCard(null);
    setDragOverCard(null);
    setDragPreview(null);
  }, [touchStartCard, leftWords, rightWords, updateTouchPosition, preventScrolling]);

  const handleTouchDrop = useCallback((targetWordId, targetSide) => {
    if (!touchStartCard) {
      return;
    }

    const targetWordData = targetSide === 'left' 
      ? leftWords.find(w => w.id === targetWordId)
      : rightWords.find(w => w.id === targetWordId);

    if (!targetWordData || matchedPairs.includes(targetWordData.id)) {
      return;
    }

    // Don't allow dropping on the same card
    if (touchStartCard.id === targetWordId) {
      return;
    }

    // Check if it's a correct match - look for any valid pair combination
    const isCorrect = wordPairs.some(pair => 
      (pair.left === touchStartCard.word && pair.right === targetWordData.word) ||
      (pair.right === touchStartCard.word && pair.left === targetWordData.word)
    );

    if (isCorrect) {
      // First add to animating pairs for the shrink animation
      setAnimatingPairs(prev => [...prev, touchStartCard.id, targetWordId]);
      
      // Update speech bubble text to show the matched words
      setMatchedWords({ left: touchStartCard.word, right: targetWordData.word });
      
      // After animation completes, add to matched pairs
      setTimeout(() => {
        setMatchedPairs(prev => {
          const newMatched = [...prev, touchStartCard.id, targetWordId];
          
          // Check if game is complete (all cards matched)
          const totalCards = leftWords.length + rightWords.length;
          if (newMatched.length === totalCards) {
            setPendingGameComplete(true);
          }
          
          return newMatched;
        });
        
        // Remove from animating pairs
        setAnimatingPairs(prev => prev.filter(id => id !== touchStartCard.id && id !== targetWordId));
        
        // Clear any hints for matched cards
        setRevealedHints(prev => prev.filter(id => {
          const leftCard = leftWords.find(w => w.id === touchStartCard.id || w.id === targetWordId);
          const rightCard = rightWords.find(w => w.id === touchStartCard.id || w.id === targetWordId);
          return leftCard ? id !== leftCard.pairId : (rightCard ? id !== rightCard.pairId : true);
        }));
      }, 600);
    } else {
      // Handle incorrect match
      // Add both cards to incorrect pairs for visual feedback
      setIncorrectPairs(prev => [...prev, touchStartCard.id, targetWordId]);
      
      // Remove incorrect state after 1 second
      setTimeout(() => {
        setIncorrectPairs(prev => prev.filter(id => id !== touchStartCard.id && id !== targetWordId));
      }, 1000);
    }
  }, [touchStartCard, leftWords, rightWords, matchedPairs, wordPairs]);

  // Party.js sparkles interval
  useEffect(() => {
    if (draggedCard) {
      sparkleIntervalRef.current = setInterval(() => {
        // Find the dragged card element and verify it's in DOM
        const draggedElement = document.querySelector('.word-card.dragging');
        
        if (draggedElement && draggedElement instanceof Element && document.contains(draggedElement)) {
          // Only create sparkles if we have valid mouse coordinates
          if (mousePosition.x > 0 && mousePosition.y > 0) {
            try {
              // Create a temporary invisible element at mouse position for sparkles
              const tempElement = document.createElement('div');
              tempElement.style.cssText = `
                position: fixed;
                left: ${mousePosition.x}px;
                top: ${mousePosition.y}px;
                width: 1px;
                height: 1px;
                pointer-events: none;
                z-index: 9999;
              `;
              document.body.appendChild(tempElement);
              
              party.sparkles(tempElement, {
                count: party.variation.range(3, 6), // Reduced count since we're generating more frequently
                speed: party.variation.range(80, 200)
              });
              
              // Remove temp element after a short delay
              setTimeout(() => {
                if (tempElement.parentNode) {
                  tempElement.parentNode.removeChild(tempElement);
                }
              }, 50);
            } catch (error) {
              console.error('❌ Interval party.sparkles error:', error);
            }
          }
        }
      }, 80); // Create sparkles every 80ms for very smooth following effect
    } else {
      if (sparkleIntervalRef.current) {
        clearInterval(sparkleIntervalRef.current);
        sparkleIntervalRef.current = null;
      }
    }

    return () => {
      if (sparkleIntervalRef.current) {
        clearInterval(sparkleIntervalRef.current);
        sparkleIntervalRef.current = null;
      }
    };
  }, [draggedCard, mousePosition]);

  // Device detection on component mount using centralized utility
  useEffect(() => {
    const cleanup = setupDeviceDetection(() => {
      // Only block actual phones, allow tablets (including portrait mode)
      setIsPhone(checkIsPhone());
      setIsTabletPortraitMode(isTabletPortrait());
      // Track tablet-sized screens using viewport width (works in browser simulation)
      setIsTabletOrSmaller(window.innerWidth <= 1024);
    });

    return cleanup;
  }, []);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      // Ensure all body styles are reset when component unmounts
      document.body.style.overflow = '';
      document.body.style.overflowY = '';
      document.body.style.overflowX = '';
      document.body.style.touchAction = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.touchAction = '';
      
      // Remove any lingering event listeners
      document.removeEventListener('touchmove', updateTouchPosition);
      document.removeEventListener('touchmove', preventScrolling);
      document.removeEventListener('touchforcechange', preventScrolling);
      document.removeEventListener('pointermove', updateMousePosition);
      document.removeEventListener('mousemove', updateMousePosition);
    };
  }, [updateTouchPosition, updateMousePosition, preventScrolling]);

  // Note: Removed scroll-to-top behavior when matchedWords is displayed
  // The success message and WEITER button are visible via z-index overlay
  // Scrolling was causing the control panel to be cut off on tablets

  // Check if card is matched (should be hidden)
  const isCardMatched = (cardId) => {
    return matchedPairs.includes(cardId);
  };

  // Check if card is being dragged
  const isCardBeingDragged = (wordId) => {
    return draggedCard && draggedCard.id === wordId;
  };

  // Check if card is drag target
  const isCardDragTarget = (wordId) => {
    return dragOverCard && dragOverCard.id === wordId;
  };

  // Check if card is currently being shown as a hint
  const isCardHinted = (pairId) => {
    return revealedHints.includes(pairId);
  };

  // Get image URL from image data
  const getImageUrl = (word) => {
    const imageData = images[word];
    if (!imageData) return null;
    
    // Only use linkedImage field
    return imageData.linkedImage;
  };

  // Handle help button click - reveal next unmatched pair (only one at a time)
  const handleHelpClick = useCallback(() => {
    // Don't allow new hints if one is already being shown
    if (revealedHints.length > 0) {
      return;
    }
    
    // Find unmatched pairs (exclude already matched pairs)
    const unmatchedPairs = wordPairs
      .map((_, index) => index)
      .filter(pairId => {
        // Check if either the left or right card with this pairId is already matched
        const leftCard = leftWords.find(w => w.pairId === pairId);
        const rightCard = rightWords.find(w => w.pairId === pairId);
        return leftCard && rightCard && 
               !matchedPairs.includes(leftCard.id) && 
               !matchedPairs.includes(rightCard.id);
      });
    
    if (unmatchedPairs.length === 0) {
      return; // No more pairs to reveal
    }
    
    // Get the next pair to reveal
    const nextPairId = unmatchedPairs[0];
    
    // Add to revealed hints (only one at a time)
    setRevealedHints([nextPairId]);
    
    // Remove the hint after 3 seconds
    setTimeout(() => {
      setRevealedHints([]);
    }, 3000);
  }, [wordPairs, matchedPairs, revealedHints, leftWords, rightWords]);

  // Handle laut selection
  const handleLautSelection = useCallback((laut) => {
    if (selectedLauts.includes(laut)) {
      setSelectedLauts(selectedLauts.filter(s => s !== laut));
    } else {
      setSelectedLauts([...selectedLauts, laut]);
    }
  }, [selectedLauts]);

  // Reset laut selection
  const resetLautSelection = useCallback(() => {
    setSelectedLauts([]);
  }, []);

  // Start game with selected lauts
  const startGameWithLauts = useCallback(() => {
    if (selectedLauts.length > 0) {
      setShowLautModal(false);

      // Filter word pairs based on selected lauts
      const filteredPairs = allWordPairs.filter(pair => {
        const leftLauts = pair.leftWordData?.laut?.map(l => l.toLowerCase()) || [];
        const rightLauts = pair.rightWordData?.laut?.map(l => l.toLowerCase()) || [];
        const pairLauts = [...leftLauts, ...rightLauts];

        return selectedLauts.some(selectedLaut =>
          pairLauts.includes(selectedLaut.toLowerCase())
        );
      });

      // Shuffle and select up to 5 pairs ensuring no duplicate words
      const shuffledPairs = filteredPairs.sort(() => Math.random() - 0.5);
      const selectedPairs = [];
      const usedWords = new Set();
      for (const pair of shuffledPairs) {
        if (usedWords.has(pair.left) || usedWords.has(pair.right)) continue;
        usedWords.add(pair.left);
        usedWords.add(pair.right);
        selectedPairs.push(pair);
        if (selectedPairs.length >= 5) break;
      }

      if (selectedPairs.length === 0) {
        setError('Keine Wortpaare für die ausgewählten Laute gefunden.');
        setShowLautModal(true);
      } else {
        setWordPairs(selectedPairs);
      }
    }
  }, [selectedLauts, allWordPairs]);

  // Continue playing with same selected lauts
  const continueWithSameLauts = useCallback(() => {
    if (selectedLauts.length === 0) {
      console.warn('No lauts selected, cannot continue');
      return;
    }

    // Filter word pairs based on currently selected lauts
    const filteredPairs = allWordPairs.filter(pair => {
      const leftLauts = pair.leftWordData?.laut?.map(l => l.toLowerCase()) || [];
      const rightLauts = pair.rightWordData?.laut?.map(l => l.toLowerCase()) || [];
      const pairLauts = [...leftLauts, ...rightLauts];

      return selectedLauts.some(selectedLaut =>
        pairLauts.includes(selectedLaut.toLowerCase())
      );
    });

    // Shuffle and select up to 5 new pairs ensuring no duplicate words
    const shuffledPairs = filteredPairs.sort(() => Math.random() - 0.5);
    const selectedPairs = [];
    const usedWords = new Set();
    for (const pair of shuffledPairs) {
      if (usedWords.has(pair.left) || usedWords.has(pair.right)) continue;
      usedWords.add(pair.left);
      usedWords.add(pair.right);
      selectedPairs.push(pair);
      if (selectedPairs.length >= 5) break;
    }

    if (selectedPairs.length === 0) {
      setError('Keine Wortpaare für die ausgewählten Laute gefunden.');
      return;
    }

    // Reset game state
    setGameComplete(false);
    setShowConfetti(false);
    setPendingGameComplete(false);

    // Set new word pairs (this will trigger initializeGame via useEffect)
    setWordPairs(selectedPairs);
  }, [selectedLauts, allWordPairs]);

  // Render Laut Selection Modal
  const renderLautSelectionModal = () => {
    return (
      <Modal show={showLautModal} onHide={onBackToGames || (() => setShowLautModal(false))} backdrop="static" keyboard={false} centered size="lg">
      <Modal.Header closeButton={true}>
        <Modal.Title>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h4 style={{ margin: 0 }}>Laute auswählen</h4>
            <OverlayTrigger
              placement="right"
              overlay={<Tooltip id="laut-info-tooltip">Wähle die Laute aus, mit denen du üben möchtest. Es werden nur Wortpaare angezeigt, die mindestens einen der ausgewählten Laute enthalten.</Tooltip>}
            >
              <div style={{ 
                width: '20px', 
                height: '20px', 
                backgroundColor: '#17a2b8', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                <Icon name="info" size="sm" />
              </div>
            </OverlayTrigger>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Wähle die Laute aus ({selectedLauts.length} ausgewählt):
        </p>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', 
          gap: '15px',
          maxHeight: '400px',
          overflowY: 'auto',
          padding: '10px'
        }}>
          {availableLauts.map(laut => {
            const isSelected = selectedLauts.includes(laut);
            
            return (
              <button
                key={laut}
                onClick={() => handleLautSelection(laut)}
                style={{
                  padding: '15px 10px',
                  border: isSelected ? '3px solid #28a745' : '2px solid #dee2e6',
                  borderRadius: '10px',
                  backgroundColor: isSelected ? '#d4edda' : '#ffffff',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: isSelected ? '#155724' : '#333',
                  transition: 'all 0.3s ease',
                  minHeight: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 1
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                {laut}
              </button>
            );
          })}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button 
          variant="outline-secondary" 
          onClick={resetLautSelection}
          disabled={selectedLauts.length === 0}
        >
          Zurücksetzen
        </Button>
        <Button 
          variant="success" 
          onClick={startGameWithLauts}
          disabled={selectedLauts.length === 0}
          style={{
            minWidth: '150px',
            backgroundColor: '#28a745',
            borderColor: '#28a745'
          }}
        >
          Spiel starten ({selectedLauts.length} Laut{selectedLauts.length !== 1 ? 'e' : ''})
        </Button>
      </Modal.Footer>
    </Modal>
    );
  };

  // Show loading state
  if (loading) {
    return (
      <div className="wordmatch-game-main-content" style={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        margin: 0,
        padding: isPhone ? '30px 15px' : '20px',
        overflow: 'visible'
      }}>
        {!isPhone && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            minHeight: '100vh',
            backgroundImage: `url(${background1})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            opacity: 0.2,
            zIndex: 0
          }} />
        )}
      <Container style={{ position: 'relative', zIndex: 1, maxWidth: '100%' }}>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '10px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          margin: '0 auto 30px',
          width: 'fit-content'
        }}>
          <div className="text-center">
            <h2 style={{ margin: 0, fontSize: '32px', fontFamily: '"Nunito Sans", sans-serif' }}>Bilder verbinden</h2>
          </div>
        </div>
        <div className="text-center py-5">
          <Spinner animation="border" role="status" className="mb-3" />
          <p>Lade Bildpaare...</p>
        </div>
      </Container>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="wordmatch-game-main-content" style={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        margin: 0,
        padding: isPhone ? '30px 15px' : '20px',
        overflow: 'visible'
      }}>
        {!isPhone && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            minHeight: '100vh',
            backgroundImage: `url(${background1})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            opacity: 0.2,
            zIndex: 0
          }} />
        )}
      <Container style={{ position: 'relative', zIndex: 1, maxWidth: '100%' }}>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '10px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          margin: '0 auto 30px',
          width: 'fit-content'
        }}>
          <div className="text-center">
            <h2 style={{ margin: 0, fontSize: '32px', fontFamily: '"Nunito Sans", sans-serif' }}>Bilder verbinden</h2>
          </div>
        </div>
        <div className="text-center py-5">
          <Alert variant="danger">
            <h4>Fehler beim Laden</h4>
            <p>{error}</p>
            <Button 
              variant="success" 
              onClick={() => {
                setShowLautModal(true);
                setSelectedLauts([]);
                fetchWordPairs();
              }}
              style={{
                minWidth: '150px',
                minHeight: '60px',
                borderRadius: '30px',
                backgroundColor: '#88D8B0',
                border: '2px solid #88D8B0',
                color: '#4A4A4A',
                fontWeight: 'bold',
                fontSize: '16px'
              }}
            >
              Erneut versuchen
            </Button>
          </Alert>
        </div>
      </Container>
      </div>
    );
  }

  return (
    <>
      {/* Laut Selection Modal */}
      {renderLautSelectionModal()}
      
      {/* Phone Warning Message */}
      {isPhone ? (
        <PhoneUnavailableMessage gameTitle="Bilder verbinden-Spiel" onBackToGames={onBackToGames} />
      ) : (
        <div className="wordmatch-game-main-content" style={{
          position: 'relative',
          minHeight: '100vh',
          width: '100%',
          margin: 0,
          padding: '20px',
          overflow: 'visible'
        }}>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            minHeight: '100vh',
            backgroundImage: `url(${background1})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            opacity: 0.2,
            zIndex: 0
          }} />
        <>
      {/* Dark overlay when showing matched words - blocks all interactions */}
      {matchedWords && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          pointerEvents: 'auto'
        }} />
      )}
      
      <div style={{
        minHeight: '100vh',
        maxHeight: '100vh',
        paddingTop: isTabletPortraitMode ? '80px' : '100px',
        paddingLeft: isTabletPortraitMode ? '15px' : '75px',
        paddingRight: isTabletPortraitMode ? '15px' : '75px',
        overflowY: 'hidden',
        overflowX: 'auto'
      }}>
        <Container style={{ 
          position: 'relative',
          overflow: 'visible',
          height: '100%',
          maxHeight: '100vh'
        }}>
        {/* Sprechhelden bubble in upper left corner - hide when game is complete */}
        {!gameComplete && (
          <img 
            src={sprechheldenbubble} 
            alt="Sprechhelden" 
            style={{
              position: 'absolute',
              left: '-2%',
              top: '-65px',
              width: '200px',
              height: 'auto',
              zIndex: matchedWords ? 1001 : 1
            }}
          />
        )}
        
        {/* Text for speech bubble - hide when game is complete */}
        {!gameComplete && (
          <div 
            className="speech-bubble-text"
            style={{
              position: 'absolute',
              left: '140px',
              top: '-80px',
              zIndex: matchedWords ? 1001 : 2,
            }}
          >
            <span style={{ fontFamily: '"Poppins", sans-serif', fontWeight: matchedWords ? 'normal' : 'bold' }}>
              {matchedWords ? (
                <>
                  <span style={{ fontFamily: '"Poppins", sans-serif', fontWeight: '600' }}>Super! Sprich die Wörter „</span><strong style={{ fontFamily: '"Poppins", sans-serif', fontWeight: '800' }}>{matchedWords.left}</strong><span style={{ fontFamily: '"Poppins", sans-serif', fontWeight: '600' }}>" und „</span><strong style={{ fontFamily: '"Poppins", sans-serif', fontWeight: '800' }}>{matchedWords.right}</strong><span style={{ fontFamily: '"Poppins", sans-serif', fontWeight: '600' }}>" laut aus!</span>
                </>
              ) : (
                'Ziehe die passenden Bilder aufeinander!'
              )}
            </span>
          </div>
        )}

        {/* Continue button when words are displayed */}
        {matchedWords && (
          <Button
            style={{
              position: 'absolute',
              left: '200px',
              top: '10px',
              zIndex: 1001,
              backgroundColor: '#198754',
              border: '2px solid rgb(136, 216, 176)',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '16px',
              borderRadius: '35px',
              padding: '10px 20px',
              fontFamily: '"Poppins", sans-serif'
            }}
            onClick={() => {
              setMatchedWords(null);
              
              // Check if game was completed and show confetti now
              if (pendingGameComplete) {
                setGameComplete(true);
                setShowConfetti(true);
                setPendingGameComplete(false);
                setTimeout(() => {
                  setShowConfetti(false);
                }, 5000);
              }
            }}
          >
            WEITER
          </Button>
        )}
      
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '10px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        margin: '0 auto 30px',
        width: 'fit-content'
      }}>
        <div className="text-center">
          <h2 style={{ margin: 0, fontSize: '32px', fontFamily: '"Nunito Sans", sans-serif' }}>Bilder verbinden</h2>
        </div>
      </div>
      
      <div className="position-relative" ref={containerRef} style={{
        minHeight: isTabletPortraitMode ? 'auto' : '400px',
        maxHeight: 'calc(100vh - 300px)',
        zIndex: 10,
        position: 'relative',
        overflow: 'visible'
      }}>
        {gameComplete ? (
          /* Game Complete Section with GameWon Character */
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            minHeight: '300px',
            position: 'relative',
            marginTop: '0'
          }}>
            {/* GameWon character with relative positioning */}
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <img 
                src={gameWon} 
                alt="Game Won Character" 
                style={{
                  width: '300px',
                  height: 'auto',
                  zIndex: 1,
                  marginTop: '0',
                  animation: 'float 3s ease-in-out infinite'
                }}
              />
              
              {/* Speech bubble text positioned relative to character */}
              <div 
                className="game-complete-speech-bubble"
                style={{
                  position: 'absolute',
                  top: '-20px',
                  left: '350px',
                  transform: 'translateX(-50%)',
                  zIndex: 2,
                  backgroundColor: '#ffffff',
                  padding: '12px 20px',
                  borderRadius: '20px',
                  border: '2px solid #333333',
                  fontSize: '18px',
                  color: '#333',
                  textAlign: 'center',
                  minWidth: '280px',
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: '600'
                }}
              >
                <span>Fantastisch! Du hast alle Paare gefunden!</span>
              </div>
            </div>
          </div>
        ) : (
          /* Game playing area */
          <div className="word-match-container">
            <div className="word-column">
              <div className="word-row">
                {leftWords.map(word => {
                  const isMatched = isCardMatched(word.id);
                  const isAnimating = animatingPairs.includes(word.id);
                  const isIncorrect = incorrectPairs.includes(word.id);
                  const isBeingDragged = isCardBeingDragged(word.id);
                  const isDragTarget = isCardDragTarget(word.id);
                  const isHinted = isCardHinted(word.pairId);
                  
                  if (isMatched && !isAnimating) return null; // Hide matched cards after animation
                  
                  return (
                    <div
                      key={word.id}
                      data-word-id={word.id}
                      data-word-side="left"
                      data-word-text={word.word}
                      draggable="true"
                      onDragStart={(e) => handleDragStart(e, word.id, 'left')}
                      onDragEnd={handleDragEnd}
                      onDragOver={handleDragOver}
                      onDragEnter={(e) => handleDragEnter(e, word.id, 'left')}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, word.id, 'left')}
                      onTouchStart={(e) => handleTouchStart(e, word.id, 'left')}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                      className={`word-card ${isBeingDragged ? 'dragging' : ''} ${isDragTarget ? 'drag-target' : ''} ${isAnimating ? 'shrinking' : ''} ${isIncorrect ? 'incorrect-match' : ''} ${isHinted ? 'hinted' : ''}`}
                    >
                      {getImageUrl(word.word) ? (
                        <img 
                          src={getImageUrl(word.word)} 
                          alt={word.word}
                          className="word-image"
                        />
                      ) : (
                        word.word
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="word-column">
              <div className="word-row">
                {rightWords.map(word => {
                  const isMatched = isCardMatched(word.id);
                  const isAnimating = animatingPairs.includes(word.id);
                  const isIncorrect = incorrectPairs.includes(word.id);
                  const isBeingDragged = isCardBeingDragged(word.id);
                  const isDragTarget = isCardDragTarget(word.id);
                  const isHinted = isCardHinted(word.pairId);
                  
                  if (isMatched && !isAnimating) return null; // Hide matched cards after animation
                  
                  return (
                    <div
                      key={word.id}
                      data-word-id={word.id}
                      data-word-side="right"
                      data-word-text={word.word}
                      draggable="true"
                      onDragStart={(e) => handleDragStart(e, word.id, 'right')}
                      onDragEnd={handleDragEnd}
                      onDragOver={handleDragOver}
                      onDragEnter={(e) => handleDragEnter(e, word.id, 'right')}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, word.id, 'right')}
                      onTouchStart={(e) => handleTouchStart(e, word.id, 'right')}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                      className={`word-card ${isBeingDragged ? 'dragging' : ''} ${isDragTarget ? 'drag-target' : ''} ${isAnimating ? 'shrinking' : ''} ${isIncorrect ? 'incorrect-match' : ''} ${isHinted ? 'hinted' : ''}`}
                    >
                      {getImageUrl(word.word) ? (
                        <img 
                          src={getImageUrl(word.word)} 
                          alt={word.word}
                          className="word-image"
                        />
                      ) : (
                        word.word
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Control Panel - Fixed at bottom on tablet screens to ensure visibility */}
      <div style={{
        backgroundColor: 'white',
        padding: '10px 20px',
        borderRadius: '10px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        width: 'fit-content',
        ...(isTabletOrSmaller ? {
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 999,
          margin: 0
        } : {
          margin: '20px auto 0'
        })
      }}>
        <div className="d-flex justify-content-center gap-3 flex-wrap">
        {onBackToGames && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="back-to-games-tooltip">Zurück zu Spielen</Tooltip>}
          >
            <Button
              onClick={onBackToGames}
              variant="secondary"
              size="lg"
              style={{
                minWidth: '60px',
                minHeight: '60px',
                borderRadius: '50%',
                boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
              }}
            >
              <LuGamepad2 size={24} />
            </Button>
          </OverlayTrigger>
        )}
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id="connections-tooltip">Verbindungen</Tooltip>}
        >
          <div style={{
            minWidth: '60px',
            minHeight: '60px',
            borderRadius: '50%',
            backgroundColor: '#f8f9fa',
            border: '2px solid #dee2e6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
            fontWeight: 'bold'
          }}>
            {Math.floor(matchedPairs.length / 2)}/{wordPairs.length}
          </div>
        </OverlayTrigger>
        
        
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id="help-tooltip">Hilfe - Zeigt ein passendes Paar</Tooltip>}
        >
          <Button
            onClick={handleHelpClick}
            variant="info"
            size="lg"
            disabled={
              (wordPairs.length > 0 && matchedPairs.length === wordPairs.length) || 
              revealedHints.length > 0
            }
            style={{
              minWidth: '60px',
              minHeight: '60px',
              borderRadius: '50%',
              border: '2px solid #17a2b8',
              opacity: (
                (wordPairs.length > 0 && matchedPairs.length === wordPairs.length) || 
                revealedHints.length > 0
              ) ? 0.5 : 1,
              padding: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <img 
              src={helpLogo} 
              alt="Hilfe" 
              style={{ 
                width: '40px', 
                height: '40px',
                objectFit: 'contain'
              }} 
            />
          </Button>
        </OverlayTrigger>
        
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id="continue-game-tooltip">Weiter spielen</Tooltip>}
        >
          <Button
            onClick={continueWithSameLauts}
            variant="success"
            size="lg"
            style={{
              minWidth: '60px',
              minHeight: '60px',
              borderRadius: '50%',
              border: '2px solid #88D8B0'
            }}
          >
            <Icon name="refresh" size="lg" />
          </Button>
        </OverlayTrigger>

        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id="change-sounds-tooltip">Laute ändern</Tooltip>}
        >
          <Button
            onClick={() => {
              setShowLautModal(true);
              setSelectedLauts([]);
              fetchWordPairs();
            }}
            variant="outline-secondary"
            size="lg"
            style={{
              minWidth: '60px',
              minHeight: '60px',
              borderRadius: '50%',
              boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
            }}
          >
            <Icon name="settings" size="lg" />
          </Button>
        </OverlayTrigger>
        </div>
      </div>

      {/* Confetti Effect */}
      {gameComplete && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={showConfetti}
          numberOfPieces={200}
          gravity={0.3}
          style={{ position: 'fixed', top: 0, left: 0, zIndex: 9999 }}
        />
      )}

      {/* Touch Drag Preview - Only visible during touch dragging */}
      {dragPreview && (
        <div
          style={{
            position: 'fixed',
            left: `${dragPreview.x}px`,
            top: `${dragPreview.y}px`,
            width: '120px',
            height: '120px',
            backgroundColor: '#e3f2fd',
            border: '3px solid #2196f3',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1rem',
            fontWeight: '600',
            color: '#4A4A4A',
            zIndex: 9999,
            pointerEvents: 'none',
            opacity: 0.9,
            transform: 'scale(1.1)',
            boxShadow: '0 8px 25px rgba(33, 150, 243, 0.4)',
            transition: 'none'
          }}
        >
          {dragPreview.imageUrl ? (
            <img 
              src={dragPreview.imageUrl} 
              alt={dragPreview.word}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '8px',
                border: '4px solid #2196f3',
                boxShadow: '0 0 20px rgba(33, 150, 243, 0.5)'
              }}
            />
          ) : (
            dragPreview.word
          )}
        </div>
      )}

      <style jsx>{`
        .speech-bubble-text {
          background-color: #ffffff;
          padding: 12px 20px;
          border-radius: 20px;
          border: 2px solid #333333;
          font-size: 20px;
          color: #333;
          text-align: center;
          min-width: 200px;
          position: relative;
        }

        .word-match-container {
          display: grid;
          grid-template-rows: auto auto;
          gap: 0.25rem;
          padding: 0.25rem;
          min-height: 400px;
          position: relative;
          z-index: 10;
          margin: 0 auto;
          max-width: 100%;
          box-sizing: border-box;
        }
        

        .word-column {
          display: flex;
          flex-direction: column;
          gap: 0.1rem;
        }

        .word-row {
          display: flex;
          flex-wrap: nowrap;
          gap: 0.05rem;
          justify-content: center;
          align-items: flex-start;
          position: relative;
          z-index: 20;
          min-height: 150px;
          overflow-x: visible;
        }

        .column-header {
          font-size: 1.2rem;
          font-weight: bold;
          color: #374151;
          text-align: center;
          margin-bottom: 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid #e5e7eb;
        }

        .word-card {
          background: #f8f9fa;
          border: 2px solid #dee2e6;
          border-radius: 12px;
          padding: 0;
          text-align: center;
          font-weight: 600;
          font-size: 1rem;
          color: #4A4A4A;
          transition: all 0.3s ease;
          user-select: none;
          position: relative;
          flex: 1 1 120px;
          min-width: 80px;
          max-width: 150px;
          aspect-ratio: 1 / 1;
          display: flex;
          align-items: center;
          justify-content: center;
          -webkit-touch-callout: none;
          -webkit-user-select: none;
          touch-action: none;
          z-index: 20;
          cursor: grab;
          opacity: 1;
          overflow: hidden;
          min-height: 80px;
        }
        
        .word-card:hover:not(.dragging):not(.shrinking) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          border-color: #88D8B0;
        }
        
        .word-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 10px;
          pointer-events: none;
          -webkit-user-select: none;
          user-select: none;
          -webkit-touch-callout: none;
        }

        /* Responsive styles for smaller screens */
        @media (max-width: 992px) {
          .word-card {
            flex: 1 1 100px;
            min-width: 70px;
            max-width: 120px;
            font-size: 0.9rem;
          }

          .word-row {
            gap: 0.04rem;
          }

          .word-match-container {
            gap: 0.2rem;
            padding: 0.2rem;
          }
        }

        /* iPad Portrait - Larger cards with minimal spacing for vertical layout */
        @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
          .word-card {
            flex: 1 1 145px;
            min-width: 130px;
            max-width: 147px;
            font-size: 1rem;
            padding: 0;
          }

          .word-row {
            gap: 0rem;
            min-height: auto;
            height: fit-content;
          }

          .word-match-container {
            gap: 0rem;
            padding: 0rem;
            min-height: auto;
            height: fit-content;
          }
        }

        @media (max-width: 767px) {
          .word-card {
            flex: 1 1 80px;
            min-width: 60px;
            max-width: 100px;
            font-size: 0.85rem;
            padding: 0;
          }

          .word-row {
            gap: 0.03rem;
          }

          .word-match-container {
            gap: 0.15rem;
            padding: 0.15rem;
          }
        }

        @media (max-width: 480px) {
          .word-card {
            flex: 1 1 70px;
            min-width: 50px;
            max-width: 80px;
            font-size: 0.75rem;
            padding: 0;
          }

          .word-row {
            gap: 0.02rem;
          }

          .word-match-container {
            gap: 0.1rem;
            padding: 0.1rem;
          }
        }

        @media (max-width: 360px) {
          .word-card {
            flex: 1 1 50px;
            min-width: 40px;
            max-width: 60px;
            font-size: 0.7rem;
            padding: 0;
          }

          .word-row {
            gap: 0.02rem;
          }

          .word-match-container {
            gap: 0.1rem;
            padding: 0.1rem;
          }
        }

        .word-card.clickable {
          cursor: pointer;
        }

        .word-card.clickable:hover {
          background: #ffffff;
          border-color: #88D8B0;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .word-card.selected {
          background: #88D8B0;
          border-color: #5cad7f;
          transform: scale(1.05);
          box-shadow: 0 4px 16px rgba(136, 216, 176, 0.4);
        }

        .word-card.clickable:not(.selected) {
          background: #e8f5e8 !important;
          border-color: #88D8B0 !important;
          border-width: 3px !important;
          animation: gentlePulse 2s infinite;
          box-shadow: 0 0 15px rgba(136, 216, 176, 0.6) !important;
        }
        
        .word-card.not-clickable {
          background: #f5f5f5 !important;
          border-color: #ccc !important;
          opacity: 0.6;
          cursor: not-allowed !important;
        }

        .word-card.connected.correct {
          background: #d4edda;
          border-color: #28a745;
          color: #155724;
        }

        .word-card.connected.incorrect {
          color: #721c24;
          box-shadow: 0 0 25px rgba(220, 53, 69, 0.8) !important;
          animation: incorrectPulse 1.5s ease-in-out 3;
        }

        .word-card.connected.incorrect.retryable {
          cursor: grab;
          position: relative;
        }

        .word-card.connected.incorrect.retryable:hover {
          background: #e6f7ff;
          border-color: #88D8B0;
          transform: translateY(-1px);
        }

        .word-card.connected.incorrect.retryable::after {
          content: "↻";
          position: absolute;
          top: -8px;
          right: -8px;
          background: #88D8B0;
          color: white;
          border-radius: 50%;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: bold;
          animation: bounce 2s infinite;
        }

        .word-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 10px;
          display: block;
          margin: 0 auto;
          background-color: #f8f9fa;
          transition: border-color 0.2s ease-in-out;
          pointer-events: none;
          -webkit-user-select: none;
          user-select: none;
          -webkit-touch-callout: none;
        }

        .connection-svg {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 15;
        }

        .connection-line {
          filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.1));
        }
        
        .connection-line.incorrect {
          filter: drop-shadow(0 0 8px rgba(239, 68, 68, 0.6));
          stroke: #ef4444 !important;
        }

        .connection-line.organic-curve {
          stroke-linecap: round;
          stroke-linejoin: round;
        }


        @keyframes pulse {
          0%, 100% {
            border-width: 3px;
          }
          50% {
            border-width: 4px;
          }
        }

        @keyframes drawLine {
          from {
            stroke-dasharray: 500;
            stroke-dashoffset: 500;
          }
          to {
            stroke-dasharray: 500;
            stroke-dashoffset: 0;
          }
        }

        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-3px);
          }
          60% {
            transform: translateY(-2px);
          }
        }

        @keyframes gentlePulse {
          0%, 100% {
            border-color: #88D8B0;
            box-shadow: 0 0 0 0 rgba(136, 216, 176, 0.4);
          }
          50% {
            border-color: #5cad7f;
            box-shadow: 0 0 0 8px rgba(136, 216, 176, 0);
          }
        }
        
        @keyframes float {
          0% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-15px);
          }
          100% {
            transform: translateY(0px);
          }
        }
        
        @keyframes incorrectPulse {
          0%, 100% {
            box-shadow: 0 0 20px rgba(220, 53, 69, 0.6);
          }
          50% {
            box-shadow: 0 0 35px rgba(220, 53, 69, 1.0);
          }
        }


        @keyframes fadeOutLine {
          0% {
            opacity: 1;
          }
          50% {
            opacity: 1;
          }
          100% {
            opacity: 0;
          }
        }

        @keyframes shrinkAndFade {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(0.5);
            opacity: 0.8;
          }
          100% {
            transform: scale(0);
            opacity: 0;
          }
        }
        
        .word-card.shrinking {
          animation: shrinkAndFade 0.6s ease-out forwards;
          pointer-events: none;
        }

        @keyframes vibrate {
          0% { transform: translateX(0); }
          10% { transform: translateX(-5px); }
          20% { transform: translateX(5px); }
          30% { transform: translateX(-5px); }
          40% { transform: translateX(5px); }
          50% { transform: translateX(-3px); }
          60% { transform: translateX(3px); }
          70% { transform: translateX(-2px); }
          80% { transform: translateX(2px); }
          90% { transform: translateX(-1px); }
          100% { transform: translateX(0); }
        }
        
        .word-card.incorrect-match {
          animation: vibrate 0.5s ease-in-out;
          box-shadow: 0 0 25px rgba(220, 53, 69, 0.8) !important;
        }
        
        .word-card.incorrect-match .word-image {
          box-shadow: 0 0 20px rgba(220, 53, 69, 0.6) !important;
          transition: box-shadow 0.2s ease-in-out;
        }

        .word-card.hinted {
          box-shadow: 0 0 25px rgba(255, 193, 7, 0.8) !important;
          transform: scale(1.05);
        }
        
        .word-card.hinted .word-image {
          box-shadow: 0 0 15px rgba(255, 193, 7, 0.5) !important;
        }

        .word-card.dragging {
          opacity: 0.4 !important;
          transform: scale(0.95) !important;
          z-index: 500 !important;
          background: #f0f0f0 !important;
          border-color: #bbb !important;
          border-width: 2px !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
          cursor: grabbing !important;
          transition: all 0.2s ease !important;
        }
        
        .word-card.dragging .word-image {
          border: 2px solid #bbb !important;
          box-shadow: 0 0 8px rgba(0, 0, 0, 0.2) !important;
          opacity: 0.6 !important;
        }

        .word-card.drag-target {
          background: #f3e5f5 !important;
          border-color: #9c27b0 !important;
          border-width: 3px !important;
          box-shadow: 0 0 20px rgba(156, 39, 176, 0.4) !important;
          animation: dragTargetPulse 1s ease-in-out infinite !important;
        }
        
        @keyframes dragTargetPulse {
          0%, 100% {
            border-color: #9c27b0;
            box-shadow: 0 0 20px rgba(156, 39, 176, 0.4);
          }
          50% {
            border-color: #7b1fa2;
            box-shadow: 0 0 30px rgba(156, 39, 176, 0.6);
          }
        }


        @media (max-width: 768px) {
          .word-match-container {
            gap: 0.15rem;
            padding: 0.15rem;
          }

          .column-header {
            font-size: 1rem;
          }

          .word-card {
            padding: 0;
            font-size: 0.85rem;
            flex: 0 1 auto;
            width: 84px;
            height: 84px;
          }

          .word-image {
            width: 100%;
            height: 100%;
            pointer-events: none;
            -webkit-user-select: none;
            user-select: none;
            -webkit-touch-callout: none;
          }

          .word-row {
            gap: 0.03rem;
            justify-content: center;
          }
        }
      `}</style>
        </Container>
      </div>
        </>
        </div>
      )}
    </>
  );
}

export default WordMatchGame;